import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, ViewEncapsulation } from '@angular/core';
import { bcDateFormats } from 'common/common/constants/date-formats';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { OperatorsService } from 'common/operators/operators.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import * as moment from 'moment';
import { SearchService } from 'common/search/search.service';
import { SearchQuery } from 'components/accordion/search-widget/model/search-query.model';

@Component({
  selector: 'app-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SearchBoxComponent implements OnInit {
  @Input()
  searchObject: SearchQuery;

  @Input()
  resultsVisible: boolean;

  @Output()
  resultsVisibleChange = new EventEmitter<boolean>();

  public searchQuery;
  public searchingLabel;

  public selectedDate;
  public selectedServiceType: string;
  public selectedOperatorId;
  public selectedDepartmentId;

  public dateRangeItems;
  public departments;
  public operators;
  public serviceTypes;

  public momentEndDate;
  public isDateDropdownCollapsed: boolean = true;
  public isLoading = true;

  constructor(private _searchService: SearchService, private _bcOperatorsService: OperatorsService, private _bcAnalytics: BcAnalytics, private _changeDetector: ChangeDetectorRef) {
    this.searchObject = this.searchObject || {
      hasResults: false,
      isSearching: false,
      query: '',
      results: []
    };
    this.searchQuery = '';
    this.searchingLabel = '';
    this.selectedServiceType = Object.keys(bcEnums.services).find(item => bcEnums.services[item] === bcEnums.services.ALL);
    this.selectedOperatorId = null;
    this.selectedDepartmentId = null;
  }

  async ngOnInit(): Promise<void> {
    this._setupDateRanges();
    this._setupServiceTypes();
    await this._setupOperators();
    await this._setupDepartments();

    this.selectedDate = this.dateRangeItems[0];
    this.isLoading = false;

    this._changeDetector.detectChanges();
  }

  _setupDateRanges() {
    this.dateRangeItems = [
      {
        id: 1,
        text: 'bcSearchBox.todayOnly',
        valueStart: moment().startOf('day'),
        valueEnd: null,
        analyticsName: 'today'
      },
      {
        id: 2,
        text: 'bcSearchBox.monthToDate',
        valueStart: moment().startOf('month'),
        valueEnd: null,
        analyticsName: 'thisMonth'
      },
      {
        id: 3,
        text: 'bcSearchBox.lastMonth',
        valueStart: moment().add(-1, 'month').startOf('month'),
        valueEnd: moment().add(-1, 'month').endOf('month'),
        analyticsName: 'lastMonth'
      },
      {
        id: 4,
        text: 'bcSearchBox.days90',
        valueStart: moment().add(-3, 'month'),
        valueEnd: null,
        analyticsName: 'last90days'
      },
      {
        id: 5,
        text: 'bcSearchBox.ytd',
        valueStart: moment().startOf('year'),
        valueEnd: null,
        analyticsName: 'thisYear'
      },
      {
        id: 6,
        text: 'bcSearchBox.lastYear',
        valueStart: moment().add(-1, 'year').startOf('year'),
        valueEnd: moment().add(-1, 'year').endOf('year'),
        analyticsName: 'lastYear'
      }
    ];

    for (const date of this.dateRangeItems) {
      date.readableStartDate = this._getReadableDate(date.valueStart);
      date.readableEndDate = this._getReadableDate(date.valueEnd);
    }
  }

  _setupServiceTypes() {
    this.serviceTypes = Object.keys(bcEnums.services)
      .filter(serviceType => ![bcEnums.services.FACEBOOK, bcEnums.services.ASYNC_MESSAGE, bcEnums.services.OMNI].includes(bcEnums.services[serviceType]))
      .map((serviceType) => {
        return {
          value: serviceType,
          label: `bcSearchBox.serviceTypes.${serviceType}`,
          selected: serviceType === this.selectedServiceType
        };
      });
  }

  async _setupOperators() {
    const operators = await this._bcOperatorsService.getOperatorsNamesByDepartmentPermission();
    this._mapOperatorsNamesList(operators);
  }

  _mapOperatorsNamesList(operators) {
    operators.sort((a, b) => {
      return a.bestName.localeCompare(b.bestName);
    });
    if (!operators.length) {
      this.operators = [{ value: null, label: 'bcSearchBox.noOperatorPermitted' }];
    }
    this.operators = [{ value: null, label: 'bcSearchBox.chooseOperator' }];
    this.operators = this.operators.concat(operators.map((operator) => {
      return {
        value: operator.operatorId,
        label: operator.bestName
      };
    }));
  }

  async _setupDepartments() {
    const departments = await this._bcOperatorsService.getDepartmentsWithOperators();
    this.departments = [{ value: null, label: 'bcSearchBox.chooseDepartment' }];
    this.departments = this.departments.concat(departments.map((department) => {
      return {
        value: department.id,
        label: department.name
      };
    }));
  }

  _getReadableDate(dateValue) {
    return dateValue ? dateValue.format('l') : '';
  }

  searchDateChanged(date) {
    if (date) {
      this.selectedDate = date;
    }
  }

  serviceTypeChanged(serviceType) {
    this.selectedServiceType = serviceType.value;
  }

  departmentChanged(department) {
    this.selectedDepartmentId = department.value;
  }

  operatorChanged(operator) {
    if (operator && operator.value) {
      this.selectedOperatorId = operator.value;
    }
  }

  toggleDateDropdown() {
    this.isDateDropdownCollapsed = !this.isDateDropdownCollapsed;
  }

  search() {
    this._clearSearch();

    this.resultsVisible = true;
    this.resultsVisibleChange.emit(this.resultsVisible);
    this.searchObject.isSearching = true;
    this.searchObject.query = this.searchQuery;
    this.searchingLabel = 'bcSearchBox.searchingLabelActive';
    this.momentEndDate = this.selectedDate.valueEnd ? moment(this.selectedDate.valueEnd) : moment();

    const searchParams = {
      startDate: moment(this.selectedDate.valueStart).utc().format(bcDateFormats.API_DATE_FORMAT),
      endDate: this.momentEndDate.utc().format(bcDateFormats.API_DATE_FORMAT),
      searchQuery: this.searchQuery,
      serviceTypes: this._getServiceTypes(),
      departmentId: this.selectedDepartmentId ? [this.selectedDepartmentId] : [],
      operatorId: this.selectedOperatorId ? [this.selectedOperatorId] : []
    };

    this._searchService.search(searchParams).then((results) => {
      this.searchingLabel = '';
      this.searchObject.results = results;
      this.searchObject.isSearching = false;
      this.searchObject.hasResults = results.length > 0;
    })
      .catch(() => {
        this.searchingLabel = '';
        this._clearSearch();
        this.searchObject.isSearching = false;
      });

    this._trackSearch();
  }

  _getServiceTypes() {
    if (bcEnums.services[this.selectedServiceType] === bcEnums.services.SMS) {
      return [bcEnums.services.SMS, bcEnums.services.FACEBOOK, bcEnums.services.ASYNC_MESSAGE];
    } else {
      return [bcEnums.services[this.selectedServiceType]];
    }
  }

  _clearSearch() {
    this.searchObject.hasResults = false;
    this.searchObject.results = [];
  }

  _trackSearch() {
    const analyticsParams = {
      agentSelected: !!this.selectedOperatorId,
      departmentSelected: !!this.selectedDepartmentId,
      dateRange: this.selectedDate.analyticsName,
      type: this.selectedServiceType
    };
    this._bcAnalytics.eventTrack('PastConversationSearch', analyticsParams);
  }

  isSearchEnabled(): boolean {
    return this.operators && this.operators.length > 1;
  }
}
